/**
 * Shared business letterhead for PDF export and print (reports).
 */

import { loadBusinessInfo } from './invoicePrintUtils';
import {
  formatSafeDateTime,
  ARABIC_DISPLAY_LOCALE,
  formatFixedDateYMD,
  formatFixedTimeHMS,
  isArabicLocale
} from './formatSafety';
import { resolvePrintAdsRgb } from './printAdsPalette';
import { getInvoicePrintLabel } from './i18n/invoicePrint';

export { loadBusinessInfo };

export function formatReportDateTime(lang = 'fr', date = new Date()) {
  if (lang === 'ar' || isArabicLocale(ARABIC_DISPLAY_LOCALE)) {
    return `${formatFixedDateYMD(date, '')} ${formatFixedTimeHMS(date, '')}`.trim();
  }
  return formatSafeDateTime(date, 'fr-MR', {}, '');
}

export function escapeReportHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function detectPdfImageFormat(src) {
  if (!src || typeof src !== 'string') return null;
  const lower = src.toLowerCase();
  if (lower.includes('image/png') || lower.endsWith('.png')) return 'PNG';
  return 'JPEG';
}

/**
 * @param {Object} data - Report payload (may already include businessInfo)
 * @returns {Promise<Object>} Enriched payload
 */
export async function enrichReportWithBusinessHeader(data = {}) {
  const businessInfo = data.businessInfo || (await loadBusinessInfo());
  return {
    ...data,
    businessInfo,
    generatedAt: data.generatedAt || new Date().toISOString()
  };
}

/**
 * Build HTML letterhead + report title block.
 */
export function buildReportLetterheadHTML(data, options = {}) {
  const lang = options.lang || 'fr';
  const isRTL = lang === 'ar';
  const business = data.businessInfo || {};
  const title = escapeReportHtml(
    isRTL ? (data.titleAr || data.title || 'تقرير') : (data.titleFr || data.title || 'Rapport')
  );
  const generatedAt = formatReportDateTime(
    lang,
    data.generatedAt ? new Date(data.generatedAt) : new Date()
  );
  const name = escapeReportHtml(business.name || business.business_name || '');
  const address = escapeReportHtml(business.address || '');
  const phone = escapeReportHtml(business.phone || '');
  const email = escapeReportHtml(business.email || '');
  const logo = business.logo ? escapeReportHtml(business.logo) : '';

  const lbl = {
    generated: getInvoicePrintLabel('reportGenerated', isRTL),
    phone: getInvoicePrintLabel('reportPhone', isRTL),
    email: getInvoicePrintLabel('reportEmail', isRTL)
  };

  const contactParts = [];
  if (phone) contactParts.push(`<span>${lbl.phone}: ${phone}</span>`);
  if (email) contactParts.push(`<span>${lbl.email}: ${email}</span>`);

  return `
  <div class="report-letterhead" dir="${isRTL ? 'rtl' : 'ltr'}">
    <div class="report-letterhead__row">
      <div class="report-letterhead__brand">
        ${logo ? `<img src="${logo}" alt="${name}" class="report-letterhead__logo" />` : ''}
        <div class="report-letterhead__identity">
          ${name ? `<div class="report-letterhead__name">${name}</div>` : ''}
          ${address ? `<div class="report-letterhead__meta">${address}</div>` : ''}
          ${contactParts.length ? `<div class="report-letterhead__contact">${contactParts.join(' &nbsp;|&nbsp; ')}</div>` : ''}
        </div>
      </div>
      <div class="report-letterhead__datetime">
        <div class="report-letterhead__datetime-label">${lbl.generated}</div>
        <div class="report-letterhead__datetime-value">${escapeReportHtml(generatedAt)}</div>
      </div>
    </div>
    <div class="report-letterhead__title">${title}</div>
  </div>`;
}

export const REPORT_LETTERHEAD_CSS = `
  .report-letterhead {
    margin-bottom: 20px;
    padding-bottom: 14px;
    border-bottom: 2px solid var(--print-primary-dark);
    page-break-after: avoid;
  }
  .report-letterhead__row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 12px;
  }
  .report-letterhead__brand {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }
  .report-letterhead__logo {
    width: 56px;
    height: 56px;
    object-fit: contain;
    flex-shrink: 0;
  }
  .report-letterhead__name {
    font-size: 18px;
    font-weight: 700;
    color: var(--print-text);
    margin-bottom: 4px;
  }
  .report-letterhead__meta,
  .report-letterhead__contact {
    font-size: 11px;
    color: var(--print-text-muted);
    line-height: 1.45;
    margin-top: 2px;
  }
  .report-letterhead__datetime {
    text-align: end;
    flex-shrink: 0;
    font-size: 11px;
    color: var(--print-text-secondary);
  }
  .report-letterhead__datetime-label {
    font-weight: 600;
    margin-bottom: 2px;
  }
  .report-letterhead__datetime-value {
    white-space: nowrap;
  }
  .report-letterhead__title {
    font-size: 15px;
    font-weight: 700;
    color: var(--print-primary);
    text-align: center;
    margin-top: 8px;
  }
`;

/**
 * Draw letterhead on jsPDF document; returns Y position for table start.
 */
export function drawReportLetterheadPDF(doc, data, options = {}) {
  const lang = options.lang || 'fr';
  const isRTL = lang === 'ar';
  const fontName = options.fontName || (isRTL ? 'NotoArabic' : 'helvetica');
  const margin = options.margin ?? 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - margin * 2;
  const business = data.businessInfo || {};
  const generatedAt = formatReportDateTime(
    lang,
    data.generatedAt ? new Date(data.generatedAt) : new Date()
  );
  const title = isRTL ? (data.titleAr || data.title || 'تقرير') : (data.titleFr || data.title || 'Rapport');

  const lbl = {
    generated: getInvoicePrintLabel('reportGenerated', isRTL),
    phone: getInvoicePrintLabel('reportPhone', isRTL),
    email: getInvoicePrintLabel('reportEmail', isRTL)
  };

  let y = margin;
  const logoSize = 22;
  const textMaxW = maxWidth * 0.58;
  const rightEdge = pageWidth - margin;
  const textX = isRTL
    ? rightEdge - (business.logo ? logoSize + 4 : 0)
    : margin + (business.logo ? logoSize + 4 : 0);
  const logoX = isRTL ? rightEdge - logoSize : margin;

  if (business.logo) {
    try {
      const format = detectPdfImageFormat(business.logo);
      if (format) {
        doc.addImage(business.logo, format, logoX, y, logoSize, logoSize);
      }
    } catch (err) {
      console.warn('[Report header] Logo skipped:', err?.message || err);
    }
  }

  doc.setFontSize(14);
  doc.setFont(fontName, 'bold');
  const businessName = business.name || business.business_name || '';
  if (businessName) {
    doc.text(String(businessName), isRTL ? rightEdge : textX, y + 5, {
      maxWidth: textMaxW,
      align: isRTL ? 'right' : 'left'
    });
  }

  doc.setFontSize(9);
  doc.setFont(fontName, 'normal');
  let lineY = y + 11;

  if (business.address) {
    const addrLines = doc.splitTextToSize(String(business.address), textMaxW);
    doc.text(addrLines, isRTL ? rightEdge : textX, lineY, { align: isRTL ? 'right' : 'left' });
    lineY += addrLines.length * 4.2;
  }

  const contactBits = [];
  if (business.phone) contactBits.push(`${lbl.phone}: ${business.phone}`);
  if (business.email) contactBits.push(`${lbl.email}: ${business.email}`);
  if (contactBits.length) {
    doc.text(contactBits.join('  |  '), isRTL ? rightEdge : textX, lineY, {
      maxWidth: textMaxW,
      align: isRTL ? 'right' : 'left'
    });
    lineY += 4.5;
  }

  doc.setFontSize(9);
  doc.setFont(fontName, 'bold');
  const dateBlock = `${lbl.generated}\n${generatedAt}`;
  doc.text(
    dateBlock,
    isRTL ? margin : pageWidth - margin,
    y + 4,
    { align: isRTL ? 'left' : 'right', maxWidth: maxWidth * 0.32 }
  );

  y = Math.max(lineY, y + logoSize) + 6;

  const [lineR, lineG, lineB] = resolvePrintAdsRgb('primaryDark');
  doc.setDrawColor(lineR, lineG, lineB);
  doc.setLineWidth(0.4);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;

  doc.setFontSize(13);
  doc.setFont(fontName, 'bold');
  const [titleR, titleG, titleB] = resolvePrintAdsRgb('primary');
  doc.setTextColor(titleR, titleG, titleB);
  doc.text(String(title), pageWidth / 2, y, { align: 'center', maxWidth });
  doc.setTextColor(0, 0, 0);
  y += 8;

  return y;
}
